import { MutationTree } from "vuex";
import {
	RootState,
	KeyboardShortcut,
	FeaturesList,
	MatrixState,
	TimeZone,
	Region,
	LoginPreferences,
	LoginResult,
	UlType
} from "./types";
import { getDefaultState } from "./state";
import { get } from "lodash";
import Vue from "vue";

export const mutations: MutationTree<RootState> = {
	login(state, data: LoginResult) {
		state.dataError = false;
		state.User = {
			loggedIn: true,
			fullName: data.loginFullname,
			userId: data.loginUserID,
			userGroupId: data.loginUserGroupID,
			error: "",
			isFullUser: data.isFullUser,
			userPermissions: data.permissions,
			useImperialMeasurements: data.useImperialMeasurements,
			canAddIndividualActionsOverride: data.canAddIndividualActionsOverride,
			allowEmptyAddressesOnAreas: data.allowEmptyAddressesOnAreas,
			permittedFileTypes: data.permittedFileTypes,
			zoomLevelToLoadMapLayers: data.zoomLevelToLoadMapLayers,
			collapseParentCategoryByDefault: data.collapseParentCategoryByDefault,
			quickControlMaxCameraRecordDuration: data.quickControlMaxCameraRecordDuration,
			mapType: data.mapType,
			mapKey: data.mapKey,
			hideLabels: data.hideLabels,
			requireMapPin: data.requireMapPin,
			alarmScreenTimeoutSeconds: data.alarmScreenTimeoutSeconds,
			alarmAgeWarningSeconds: data.alarmAgeWarningSeconds,
			dbFederationEnabled: data.dbFederationEnabled || false, // default to false
			mediaMatrixState: MatrixState.closed,
			dynamicLinkConfig: data.dynamicLinkConfig,
			mediaMatrixConfig: data.mediaMatrixConfig,
			requireReasonUpdatingClearanceOnReaderGroups: data.requireReasonUpdatingClearanceOnReaderGroups,
			additionalActionsOnForceRestoredAlarms: data.additionalActionsOnForceRestoredAlarms,
			quickControlPrefs: {
				warning: data.quickControlWarning,
				regex: data.quickControlRegex
			}
		};
	},
	logout(state) {
		state.User = {
			userId: null,
			userGroupId: null,
			loggedIn: false,
			fullName: "",
			error: "",
			isFullUser: false,
			userPermissions: {
				canManageContactRoles: false,
				canEditSettings: false,
				canHandleAlarms: false,
				canInteractHighPriorityBanner: false,
				canViewReports: false,
				canAddIndividualActions: false,
				canEditUserPermissions: false,
				canEditUserGroups: false,
				canViewSiteSetup: false,
				canEditSiteSetup: false,
				canRaiseActivityLog: false,
				canToggleLogVisibility: false,
				canEditOwnActivityLog: false,
				canEditOtherActivityLogs: false,
				isAdmin: false,
				canDisarmSites: false,
				canDisarmSitesExtended: false,
				canSetResponseToMasked: false,
				canSetSiteToOnTest: false,
				canSetSiteToAuditMode: false,
				canViewEvents: false,
				canAccessQuickControl: false,
				canViewMediaMatrisControls: false,
				canAccessManualRaise: false,
				canAccessManualTour: false,
				canViewSitRep: false,
				canUseMobile: false,
				canViewMobileRaise: false,
				canEditMobileRaise: false,
				canDownloadEventSearchSitRep: false,
				canDownloadEventSearchMedia: false,
				canViewLicenseDetails: false,
				canViewActivityLogManagement: false,
				canPerformVideoReport: false,
				canPerformVideoReportUpload: false,
				canProcessActiveAlarms: false,
				canDownloadSingleClip: false,
				canViewExternalMapLayers: false,
				canEditExternalMapLayers: false,
				canEditInsightsTitle: false,
				canExtendAlerts: false,
				canEditMaps: false,
				canEditSystemSettings: false,
				canEditEventCategorySetup: false,
				canEditGuardTours: false,
				canViewGuardTours: false,
				canEnableDisableUsers: false,
				canAssignGuardTour: false,
				canConductGuardTour: false,
				canClaimFromMobileRaise: false,
				canMobileDispatch: false,
				canAccessQuickControlEvents: false,
				canAccessVideowall: false,
				canViewSystemEvents: false,
				canViewSiteArmedState: false,
				canEndMultipleAlarms: false,
				canChangeGuardType: false,
				canPerformMatrixSnapshot: false,
				canViewAttachedMedia: false,
				canTriggerTestAlarm: false,
				canViewCCureLockdown: false,
				canEditCCureLockdown: false,
				canPromoteSelfToIC: false,
				canPerformQuickControlWithoutFootage: false,
				canViewCameras: false,
				canViewExternalMenuLinks: false,
				canEditExternalMenuLinks: false,
				canClaimEvents: false,
				canSelectConfigByCheckbox: false,
				canCreateCustomSQLReports: false,
				canShareReports: false,
				canShareReportsPublicly: false,
				canDispatchAssessor: false,
				canManualRaiseRegionally: false,
				canMaskPointsRegionally: false,
				canArmDisarmRegionally: false,
				canViewActionPlans: false,
				canEditActionPlans: false,
				canViewDispatchSetup: false,
				canEditDispatchSetup: false,
				canQuickControlRegions: false,
				canResetCCureRegions: false,
				canUseRegionsInViews: false,
				canManageDomains: false,
				canEditDomainGroups: false,
				canViewDomainGroups: false,
				canViewBadgeActivity: false,
				canEditLockUnlock: false,
				canSetDuressEventType: false,
				canRaiseDuress: false
			},
			useImperialMeasurements: false,
			mapType: "",
			hideLabels: false,
			requireMapPin: false,
			alarmScreenTimeoutSeconds: null,
			alarmAgeWarningSeconds: null,
			canAddIndividualActionsOverride: false,
			allowEmptyAddressesOnAreas: false,
			permittedFileTypes: "",
			zoomLevelToLoadMapLayers: 0,
			dbFederationEnabled: false,
			quickControlMaxCameraRecordDuration: 0,
			mediaMatrixState: 0,
			dynamicLinkConfig: null,
			mediaMatrixConfig: {
				areasExpanded: false,
				camerasExpanded: false,
				clipsExpanded: false,
				controlsExpanded: false,
				videoToursExpanded: false,
				filesExpanded: false,
				audioExpanded: false
			},
			requireReasonUpdatingClearanceOnReaderGroups: false,
			additionalActionsOnForceRestoredAlarms: false,
			quickControlPrefs: {
				warning: "",
				regex: ""
			}
		};
		state.UserGroups = [];
	},
	// Stores the local login before we login to a different region
	storeLocalLogin(state) {
		state.localUserData = state.User;
	},
	// Resets the user data back to whatever we stored previously
	resetUserData(state) {
		state.User = state.localUserData;
		state.localUserData = null;
	},
	setAppInitialisedFlag(state, status: boolean) {
		state.appInitialised = status;
	},
	setDataError(state, hasError) {
		state.dataError = hasError;
	},
	setUserGroups(state, groups) {
		state.UserGroups = groups;
	},
	setShortcuts(state, shortCuts: KeyboardShortcut[]) {
		// Filter out any shortcuts that may be disabled via feature flagging
		const validShortcuts = shortCuts.filter(sc => {
			// if the shortcut has no feature flag, always include it
			if (!sc.featureFlag) {
				return true;
			} else {
				return get(state.featuresList, sc.featureFlag, false);
			}
		});

		state.keyboardShortcuts = validShortcuts;
	},
	setLegendKeys(state, legends) {
		state.legendKeys = legends;
	},
	setRoutingGroupsLegend(state, show: boolean) {
		state.showRoutingGroupsLegend = show;
	},
	setTimeZoneAbbr(state, timeZoneAbbr: string) {
		state.timeZoneAbbr = timeZoneAbbr;
	},
	setMapType(state, mapType: string) {
		state.User.mapType = mapType;
	},
	setRoutingGroups(state, routeGroups) {
		state.routingGroups = routeGroups;
	},
	setUserAlarmScreenTimeoutSeconds(state, alarmScreenTimeoutSeconds) {
		state.User.alarmScreenTimeoutSeconds = alarmScreenTimeoutSeconds;
	},
	setAppVersion(state, version: string) {
		state.version = version;
	},
	setServerName(state, serverName: string) {
		state.serverName = serverName;
	},
	setMediaMatrixState(state, status: MatrixState) {
		state.User.mediaMatrixState = status; // 0 = closed, 1 = initializing, 2 = open
	},
	setFeaturesList(state, config: FeaturesList) {
		state.featuresList = config;
	},
	setTimeZones(state, config: TimeZone[]) {
		state.timeZones = config;
	},
	setUlTypes(state, config: UlType[]) {
		state.ulTypes = config;
	},
	setAccountCreationStatus(state, status: boolean) {
		state.accountCreationStatus = status;
	},
	resetState(state) {
		Object.assign(state, getDefaultState());
	},
	setPreviousRoute(state, fullPath: string) {
		state.previousRoute = fullPath;
	},
	setCurrentClipCells(state, clipCells: any) {
		state.currentClipCells = clipCells;
	},
	setIsLoading(state, loading) {
		state.loading = loading;
	},
	setLoginPreferences(state, loginPreferences: LoginPreferences) {
		state.loginPreferences = loginPreferences;
	}
};
